import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Sparkles, BookOpen, Target, FileText, CheckCircle2, ListChecks, AlertTriangle,
  Lightbulb, Quote, Loader2, ChevronRight, ExternalLink, ShieldCheck, IndianRupee, Wallet,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PreviewUnlockOverlay from '../components/PreviewUnlockOverlay';
import {
  RolePreviewLayout,
  ScrollSection,
  SectionHeader,
  BulletList,
  Pills,
} from '../components/RolePreviewLayout';
import { Button } from '../components/ui/button';
import { normalizeEntrepreneurship } from '../lib/entrepreneurshipNormalizer';
import { loadContent } from '../lib/contentLoader';

const SECTIONS = [
  { id: 'snapshot', label: 'Snapshot', icon: Sparkles },
  { id: 'figures', label: 'Key Figures', icon: Wallet },
  { id: 'support', label: 'Support Types', icon: Target },
  { id: 'best-for', label: 'Best Suited For', icon: CheckCircle2 },
  { id: 'fit-ideas', label: 'Fit Business Ideas', icon: Lightbulb },
  { id: 'docs', label: 'Documents', icon: FileText },
  { id: 'process', label: 'Apply Process', icon: ListChecks },
  { id: 'warning', label: 'Warning', icon: AlertTriangle },
  { id: 'links', label: 'Official Links', icon: ExternalLink },
  { id: 'takeaway', label: 'Final Takeaway', icon: Quote },
];

const REGION_ACCENT = {
  'Andhra Pradesh': { from: 'from-yellow-600', via: 'via-amber-700', to: 'to-orange-800' },
  'Telangana':      { from: 'from-emerald-600', via: 'via-green-700', to: 'to-teal-800' },
  'Central':        { from: 'from-indigo-600', via: 'via-blue-700', to: 'to-slate-800' },
};
const getAccent = (r) => REGION_ACCENT[r] || REGION_ACCENT['Central'];

const SchemeDetail = () => {
  const { schemeId } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent('entrepreneurship.json')
      .then((d) => {
        if (d && !d.__locked) setPkg(normalizeEntrepreneurship(d));
        setLoading(false);
      })
      .catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [schemeId]);

  const s = useMemo(() => pkg?.schemes.find(x => x.id === schemeId), [pkg, schemeId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-amber-600" size={32} /></div>;
  if (!s) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
        <p className="text-gray-700 font-semibold mb-2">Scheme not found</p>
        <Button asChild><Link to="/explore/entrepreneurship">Back to schemes</Link></Button>
      </div>
    );
  }

  const accent = getAccent(s.region);

  return (
    <PreviewUnlockOverlay
      pathKey="entrepreneurship"
      previewId={String(s.id)}
      itemName={s.title}
      itemType="scheme"
    >
    <div className="min-h-screen bg-white" data-testid="scheme-detail-page">
      <Navbar />

      <RolePreviewLayout
        sections={SECTIONS}
        heroAccent={accent}
        heroBadges={[
          { text: `${s.emoji} ${s.region}`, tone: 'solid' },
          { text: s.type },
          ...(s.verified ? [{ text: '✓ Verified' }] : []),
        ]}
        heroEyebrow="Government Scheme · OwnPath Hub"
        title={s.title}
        description={s.snapshot}
        onBack={() => navigate('/explore/entrepreneurship')}
        backLabel="Back to all schemes"
      >
        {/* 1. Snapshot */}
        <ScrollSection id="snapshot">
          <SectionHeader icon={Sparkles} title="Snapshot" subtitle="Quick facts" />
          <div className="grid sm:grid-cols-2 gap-3">
            {s.status && <KV k="Policy status" v={s.status} />}
            {s.region && <KV k="Region" v={s.region} />}
            {s.type && <KV k="Type" v={s.type} />}
            {s.verified !== undefined && <KV k="Verified" v={s.verified ? 'Verified by editorial team' : 'Information may change'} />}
          </div>
        </ScrollSection>

        {/* 1b. Key Figures */}
        {s.keyFigures?.length > 0 && (
          <ScrollSection id="figures">
            <SectionHeader icon={Wallet} title="Key Figures" subtitle="The numbers that matter — verify on the official portal" />
            <div className="grid sm:grid-cols-2 gap-3" data-testid="scheme-key-figures">
              {s.keyFigures.map((f, i) => (
                <div key={i} className="p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 flex gap-3">
                  <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <IndianRupee size={15} />
                  </span>
                  <p className="text-sm font-semibold text-gray-900 leading-relaxed">{f}</p>
                </div>
              ))}
            </div>
          </ScrollSection>
        )}

        {/* 2. Support Types */}
        {s.supportTypes?.length > 0 && (
          <ScrollSection id="support">
            <SectionHeader icon={Target} title="Support Types" subtitle="What the scheme actually offers" />
            <BulletList items={s.supportTypes} dotColor="bg-amber-500" />
          </ScrollSection>
        )}

        {/* 3. Best Suited For */}
        {s.bestFor?.length > 0 && (
          <ScrollSection id="best-for">
            <SectionHeader icon={CheckCircle2} title="Best Suited For" />
            <Pills items={s.bestFor} tone="emerald" />
          </ScrollSection>
        )}

        {/* 4. Fit Business Ideas */}
        {s.fitBusinessIdeas?.length > 0 && (
          <ScrollSection id="fit-ideas">
            <SectionHeader icon={Lightbulb} title="Fit Business Ideas" subtitle="Examples this scheme tends to suit" />
            <Pills items={s.fitBusinessIdeas} tone="blue" />
          </ScrollSection>
        )}

        {/* 5. Documents */}
        {s.documents?.length > 0 && (
          <ScrollSection id="docs">
            <SectionHeader icon={FileText} title="Documents You Will Need" />
            <BulletList items={s.documents} dotColor="bg-indigo-500" />
          </ScrollSection>
        )}

        {/* 6. Apply Process */}
        {s.applyProcess?.length > 0 && (
          <ScrollSection id="process">
            <SectionHeader icon={ListChecks} title="How to Apply" subtitle="Step-by-step process" />
            <ol className="space-y-3">
              {s.applyProcess.map((step, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-white border-2 border-gray-100 rounded-xl">
                  <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 text-amber-800 text-xs font-bold shrink-0">{i + 1}</span>
                  <span className="text-base text-gray-800 leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </ScrollSection>
        )}

        {/* 7. Warning */}
        {s.warning && (
          <ScrollSection id="warning">
            <SectionHeader icon={AlertTriangle} title="Warning" subtitle="Read this before applying" />
            <div className="p-5 bg-rose-50 border-l-4 border-rose-400 rounded-r-xl flex gap-3">
              <AlertTriangle className="text-rose-600 shrink-0 mt-0.5" size={18} />
              <p className="text-base text-rose-900 leading-relaxed">{s.warning}</p>
            </div>
          </ScrollSection>
        )}

        {/* 8. Official Links */}
        {s.officialGovernmentLinks?.length > 0 && (
          <ScrollSection id="links">
            <SectionHeader icon={ExternalLink} title="Official Government Links" subtitle="Always verify on these portals before applying" />
            <div className="grid sm:grid-cols-2 gap-2.5">
              {s.officialGovernmentLinks.map((lnk, i) => (
                <a
                  key={i}
                  href={lnk.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`gov-link-${i}`}
                  className="group flex items-start gap-2.5 p-4 bg-amber-50 hover:bg-amber-100 border-2 border-amber-200 hover:border-amber-400 rounded-2xl transition-all"
                >
                  <ExternalLink size={16} className="text-amber-700 mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform" />
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-gray-900 leading-snug">{lnk.label}</p>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mt-1">{lnk.type?.replace(/-/g, ' ')}</p>
                  </div>
                </a>
              ))}
            </div>
            {s.sourceIds?.length > 0 && (
              <p className="mt-4 text-[11px] text-gray-500 italic">
                Source documents: {s.sourceIds.join(' · ')}
              </p>
            )}
          </ScrollSection>
        )}

        {/* 9. Final Takeaway */}
        <ScrollSection id="takeaway">
          <div className={`p-7 md:p-8 bg-gradient-to-br ${accent.from} ${accent.via} ${accent.to} rounded-3xl text-white`}>
            <ShieldCheck className="text-white/80 mb-3" size={28} />
            <p className="text-lg md:text-xl leading-[1.55] font-medium mb-5">
              {s.title} can reduce capital pressure, but only when documentation, eligibility, and execution are tight. Always verify the latest amounts and deadlines on the official portal before applying.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/explore/entrepreneurship')}
                className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white text-gray-900 hover:bg-gray-100 px-4 py-2 rounded-full transition-colors"
              >
                Explore more schemes <ChevronRight size={14} />
              </button>
              {s.officialGovernmentLinks?.[0]?.url && (
                <a
                  href={s.officialGovernmentLinks[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/15 backdrop-blur-sm text-white hover:bg-white/25 px-4 py-2 rounded-full border border-white/30 transition-colors"
                >
                  Open official portal <ExternalLink size={13} />
                </a>
              )}
            </div>
          </div>
        </ScrollSection>
      </RolePreviewLayout>

      <Footer />
    </div>
    </PreviewUnlockOverlay>
  );
};

const KV = ({ k, v }) => (
  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
    <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1.5">{k}</p>
    <p className="text-base text-gray-900 leading-relaxed">{v}</p>
  </div>
);

export default SchemeDetail;
